import { Locale, replaceTldForLocale } from '@marketing-sites/utils'

const domain = process.env['NEXT_PUBLIC_DOMAIN'] ?? 'readingeggs.com.au'

export interface LocalizedVersion {
  locale: Locale
  href: string
}

/**
 * Builds a URL for a given pathname and locale by replacing the TLD
 */
export function buildURLForLocale(pathname: string, locale: Locale): string {
  const url = replaceTldForLocale(domain, locale)
  url.pathname = pathname
  return url.toString()
}

/**
 * Builds an article URL for a given slug and locale
 */
export function buildArticleURLForLocale(
  articleSlugForLocale: string,
  locale: Locale,
): string {
  return buildURLForLocale(`/articles/${articleSlugForLocale}/`, locale)
}

/**
 * Slugs for each locale for an article
 */
export interface ArticleLocalizedSlugs {
  slugForEnAU?: string | null | undefined
  slugForEnUS?: string | null | undefined
  slugForEnCA?: string | null | undefined
  slugForEnGB?: string | null | undefined
  slugForEnNZ?: string | null | undefined
  slugForEnZA?: string | null | undefined
}

/**
 * Builds an array of localized versions for articles with per-locale slugs
 */
export function buildArticleLocalizedVersions(
  slugs: ArticleLocalizedSlugs,
): LocalizedVersion[] {
  return [
    {
      locale: Locale.au,
      href: buildArticleURLForLocale(slugs.slugForEnAU ?? '', Locale.au),
    },
    {
      locale: Locale.us,
      href: buildArticleURLForLocale(slugs.slugForEnUS ?? '', Locale.us),
    },
    {
      locale: Locale.ca,
      href: buildArticleURLForLocale(slugs.slugForEnCA ?? '', Locale.ca),
    },
    {
      locale: Locale.uk,
      href: buildArticleURLForLocale(slugs.slugForEnGB ?? '', Locale.uk),
    },
    {
      locale: Locale.nz,
      href: buildArticleURLForLocale(slugs.slugForEnNZ ?? '', Locale.nz),
    },
    {
      locale: Locale.za,
      href: buildArticleURLForLocale(slugs.slugForEnZA ?? '', Locale.za),
    },
  ]
}

/**
 * Builds an array of localized versions for pages with the same path across all locales
 */
export function buildLocalizedVersions(pathname: string): LocalizedVersion[] {
  return [
    { locale: Locale.au, href: buildURLForLocale(pathname, Locale.au) },
    { locale: Locale.us, href: buildURLForLocale(pathname, Locale.us) },
    { locale: Locale.ca, href: buildURLForLocale(pathname, Locale.ca) },
    { locale: Locale.uk, href: buildURLForLocale(pathname, Locale.uk) },
    { locale: Locale.nz, href: buildURLForLocale(pathname, Locale.nz) },
    { locale: Locale.za, href: buildURLForLocale(pathname, Locale.za) },
  ]
}

/**
 * Gets the x-default href for a given pathname (defaults to US locale)
 */
export function getXDefaultHref(pathname: string): string {
  return buildURLForLocale(pathname, Locale.us)
}

/**
 * Gets the x-default href for an article
 */
export function getArticleXDefaultHref(
  slugForEnUS: string | null | undefined,
): string {
  return buildArticleURLForLocale(slugForEnUS ?? '', Locale.us)
}
